'use client'

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'

import { notFound } from 'next/navigation'

import { isDirectorTaskFromRecord } from '@/components/business/prompt/controls/director/director-data'
import { useAppContext } from '@/components/providers/app-provider'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { useWork } from '@/hooks'
import { useWorkReset } from '@/hooks/work/use-work-reset'
import { useRouter } from '@/i18n/navigation'
import { CreationDetail } from '@/lib/api'
import { useCreationDetail } from '@/lib/api/hooks/creation/use-creation-detail'
import { TaskSceneParams } from '@/lib/api/models/work'
import { parseDirectorPayload } from '@/lib/utils/director-restore'
import { safeJsonParse } from '@/lib/utils/transform'
import { useParamsStore, useImagesStore, useReferenceMediaStore } from '@/stores'

import { DesktopWork } from './desktop/desktop-work'
import { MobileWork } from './mobile/mobile-work'
import { useWorkSwiper } from './mobile/use-work-swiper'
import { WorkProvider } from './work-context'

interface WorkDialogProps {
  id?: string
  intercepted?: boolean
  /** 服务端预取的作品详情，保证首屏直接出封面而不是空白 */
  initialData?: CreationDetail
}

export const WorkDialog = ({ id, intercepted = false, initialData }: WorkDialogProps) => {
  if (!id) {
    notFound()
  }
  const router = useRouter()
  const { isMobile } = useAppContext()
  const { setTaskParams } = useWork()
  const { list, initialIndex, hasData } = useWorkSwiper(id)

  const skipResetRef = useRef(false)
  const hasRestoredRef = useRef(false)

  // 不再保存状态，返回时直接清空
  const savedStateRef = useRef<boolean>(false)

  const { reset } = useWorkReset({ auto: false, keepPrompt: true })

  const setSkipReset = useCallback((skip: boolean) => {
    skipResetRef.current = skip
  }, [])

  // 返回时清空所有输入数据
  const restoreSavedState = useCallback(() => {
    reset()
    // 强制清空所有输入数据
    useParamsStore.getState().setVideoPrompt('')
    useParamsStore.getState().setReferencePrompt('')
    useParamsStore.getState().clearDirectorState()
    useImagesStore.getState().resetImages()
    useReferenceMediaStore.getState().resetReferenceMedia()
  }, [reset])

  // 从列表中查找当前作品作为初始数据
  // 这样可以立即显示内容，同时在后台获取最新的详细数据
  const listData = list.find(item => item.jobId === id || item.id.toString() === id)

  const { data: apiData } = useCreationDetail(id, { initialData: listData ?? initialData })

  // 合并数据：封面和视频地址优先使用列表数据，其他字段使用接口数据
  // 只有当列表数据中没有这些字段时，才使用接口数据
  const data = useMemo(() => {
    if (!apiData) return listData

    return {
      ...apiData,
      // 封面：优先使用列表数据
      thumbnail: listData?.thumbnail || apiData.thumbnail,
      // 视频地址：优先使用列表数据
      videoUrl: listData?.videoUrl || apiData.videoUrl,
      // cover 是 getter，展开时不会复制，需要手动添加
      cover: apiData.cover
    }
  }, [apiData, listData])

  const handleOpenChange = (open: boolean) => {
    if (!open && intercepted) {
      if (!skipResetRef.current) {
        restoreSavedState()
        hasRestoredRef.current = true
      }
      router.back()
    }
  }

  // 标记组件已挂载
  useLayoutEffect(() => {
    savedStateRef.current = true
  }, [])

  // 监听页面关闭/刷新，清理 work 页面的提示词
  useEffect(() => {
    const handleBeforeUnload = () => {
      // 直接操作 localStorage，清空提示词
      try {
        const stored = localStorage.getItem('params-storage')
        if (stored) {
          const data = JSON.parse(stored)
          if (data.state) {
            data.state.videoPrompt = ''
            data.state.referencePrompt = ''
            localStorage.setItem('params-storage', JSON.stringify(data))
          }
        }
      } catch (e) {
        // ignore
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  useEffect(() => {
    if (!data?.extra) return

    const rawSceneParams =
      typeof data.extra.sceneParams === 'string'
        ? data.extra.sceneParams
        : JSON.stringify(data.extra.sceneParams ?? {})

    const sceneParams = safeJsonParse<TaskSceneParams>(rawSceneParams) || new TaskSceneParams()

    if (
      isDirectorTaskFromRecord({
        sceneId: data.extra.sceneId,
        engine: data.extra.engine,
        sceneParams
      })
    ) {
      const directorPayload = parseDirectorPayload(sceneParams, rawSceneParams)
      if (directorPayload) {
        sceneParams.ext = JSON.stringify(directorPayload)
      }
    }

    setTaskParams({
      sceneId: data.extra.sceneId,
      model: data.extra.model,
      sceneParams
    })
  }, [data, setTaskParams])

  useEffect(() => {
    return () => {
      if (hasRestoredRef.current) {
        skipResetRef.current = false
        return
      }

      if (!skipResetRef.current) {
        restoreSavedState()
      }

      skipResetRef.current = false
      hasRestoredRef.current = false
    }
  }, [restoreSavedState])

  // 移动端使用全屏覆盖层（避免 Dialog 的滚动锁定阻止 Swiper 滑动）
  if (isMobile) {
    return (
      <WorkProvider
        value={{
          data,
          list,
          initialIndex,
          hasData,
          setSkipReset
        }}
      >
        <div
          className="fixed inset-0 z-modal h-[100dvh] w-screen overflow-hidden bg-primary"
          style={{ touchAction: 'manipulation' }}
        >
          {hasData ? (
            <MobileWork initialIndex={initialIndex} list={list} />
          ) : data ? (
            <MobileWork data={data} initialIndex={0} list={[data]} />
          ) : null}
        </div>
      </WorkProvider>
    )
  }

  // 桌面端使用 Dialog
  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        className="z-modal h-full w-screen max-w-screen rounded-none border-none p-0 sm:max-w-screen"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only" />
        <DialogDescription className="sr-only" />
        <WorkProvider
          value={{
            data,
            list,
            initialIndex,
            hasData,
            setSkipReset
          }}
        >
          <div className="flex-1 overflow-hidden bg-primary">
            {data ? <DesktopWork data={data} /> : null}
          </div>
        </WorkProvider>
      </DialogContent>
    </Dialog>
  )
}
